import React, { FC, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { containerGroupedByAxis, getHighestDraughtForWaterType, sideWeights } from "../../utils/vesselWeightHelpers";
import SideWeightIndicator, { Side } from "./SideWeightDistribution";
import { normalizeId } from "../../utils/normalize";
import { getDepartureStabilityState } from "../../utils/vesselState";

const WEIGHT_Y_AXIS_TYPE = "y_axis";
const DEFAULT_BEAM_WARNINGS = [20, 35, 50, 60];
const TILT_MAX_DEGREES = 5;
const CONTAINER_HEIGHT = 2.89;
const BOX_HEIGHT = 17;
const DEFAULT_WATER_FRAC = 0.12;

const VIEW_W = 150;
const VIEW_H = 150;
const KEEL_Y = 142;
const TOP_Y = 16;
const MAX_COG_LINE_Y = TOP_Y - 4;
const RANGE = KEEL_Y - TOP_Y;
const CENTER_X = 75;
const DECK_Y = KEEL_Y - Math.round(0.3 * RANGE);
const HULL_DECK_LEFT = 19;
const HULL_DECK_RIGHT = 131;
const HULL_KEEL_LEFT = 35;
const HULL_KEEL_RIGHT = 115;
const WHEELHOUSE_EXTRA = 12;
const MIN_WHEELHOUSE_H = 22;
const PILLAR_WIDTH = 12;
const HEAD_HEIGHT = 18;
const HEAD_FRAC = 0.55;
const DECK_WIDTH = HULL_DECK_RIGHT - HULL_DECK_LEFT;

interface Props {
  vessel: any;
  containers: any[];
  terminalCall?: any;
}

const VesselTiltFrontProfile: FC<Props> = ({ vessel, containers, terminalCall }) => {
  const { t } = useTranslation();

  const containerGroupedByYAxis = useMemo(
    () => containerGroupedByAxis(vessel, containers, terminalCall, WEIGHT_Y_AXIS_TYPE),
    [vessel, containers, terminalCall]
  );

  const beamWarnings = useMemo(() => {
    const configured = vessel?.beamWarnings;
    return Array.isArray(configured) && configured.length > 0
      ? configured
      : DEFAULT_BEAM_WARNINGS;
  }, [vessel]);

  const stabilityState = useMemo(
    () => getDepartureStabilityState(vessel, terminalCall),
    [vessel, terminalCall]
  );
  const stability = stabilityState?.stability;
  const cogLoaded = Number(stability?.cog_loaded?.z ?? 0);
  const cogMax = Number(stability?.cog_loaded_max?.z ?? 0);
  const draughtCm = Number(stabilityState?.statistics?.draught ?? 0);

  const hasMaxKg = cogMax !== 0 && cogMax !== 1;
  const isOverLoad = hasMaxKg && cogLoaded > cogMax;
  const maxDraught = getHighestDraughtForWaterType(
    "fresh",
    vessel?.totalWeightDraughtTable
  );
  const tonnage = Number(stabilityState?.statistics?.total_weight ?? 0);
  const isDraughtOver =
    draughtCm === 0 || Boolean(maxDraught && draughtCm > maxDraught);
  const isTotalWeightOver =
    tonnage === 0 || Boolean(vessel?.maxWeight && tonnage > vessel.maxWeight);
  const isStable = !isOverLoad && !isDraughtOver && !isTotalWeightOver;
  const cogColor = isStable ? "#16a34a" : "#dc2626";
  const cogBgColor = isStable ? "#dcfce7" : "#fde8e8";
  const cogTextColor = isStable ? "#166534" : "#b91c1c";

  const baseOffset = Number(vessel?.baseOffset ?? 0.5);

  const { totalHeight, cogFrac } = useMemo(() => {
    let usedLayers = 1;
    containers.forEach((container) => {
      const layer = Number(container?.position?.[0]);
      if (Number.isFinite(layer) && layer > 0) usedLayers = Math.max(usedLayers, layer);
    });
    const totalHeight = baseOffset + usedLayers * CONTAINER_HEIGHT;
    if (!Number.isFinite(totalHeight) || totalHeight <= 0) {
      return { totalHeight: 0, cogFrac: 0, maxCogFrac: 0 };
    }
    const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
    return {
      totalHeight,
      cogFrac: clamp(cogLoaded / totalHeight),
      maxCogFrac: clamp(cogMax / totalHeight),
    };
  }, [vessel, containers, cogLoaded, cogMax, baseOffset]);

  const slotCount = useMemo(() => {
    const firstLayer = vessel?.positions?.[0];
    if (!Array.isArray(firstLayer) || firstLayer.length === 0) return 4;
    return Math.max(1, ...firstLayer.map(Number).filter(Number.isFinite));
  }, [vessel]);

  const containerColumns = useMemo(() => {
    const width = DECK_WIDTH / slotCount;
    const columns = [];
    for (let slot = 1; slot <= slotCount; slot += 1) {
      // Slot 1 = port outer (left side), highest slot = starboard outer (right side).
      columns.push({
        slot,
        x: HULL_DECK_LEFT + (slot - 1) * width,
        width,
      });
    }
    return columns;
  }, [slotCount]);

  const maxLayerBySlot = useMemo(() => {
    const vesselId = normalizeId(vessel?.["@id"]);
    const layers = new Array(slotCount).fill(0);
    containers.forEach((container) => {
      const position = container?.position;
      if (!position || position[3] !== vesselId) return;
      const slot = Number(position[2]);
      const layer = Number(position[0]);
      if (Number.isFinite(slot) && slot >= 1 && slot <= slotCount && Number.isFinite(layer) && layer > 0) {
        layers[slot - 1] = Math.max(layers[slot - 1], layer);
      }
    });
    return layers;
  }, [vessel, containers, slotCount]);

  const maxLayerAll = Math.max(...maxLayerBySlot, 0);

  const pxPerMeter = totalHeight > 0 ? RANGE / totalHeight : 0;
  const boxHeight = BOX_HEIGHT;
  const stackBottomY = KEEL_Y - baseOffset * pxPerMeter;
  const loadedStackHeight = maxLayerAll * boxHeight;

  const wheelhouse = useMemo(() => {
    const headWidth = Math.round(DECK_WIDTH * HEAD_FRAC);
    const headX = HULL_DECK_LEFT + Math.round((DECK_WIDTH - headWidth) / 2);
    const stackTopY = stackBottomY - loadedStackHeight;
    const headTop = Math.max(
      TOP_Y,
      Math.min(stackTopY - WHEELHOUSE_EXTRA, DECK_Y - MIN_WHEELHOUSE_H)
    );
    return {
      headX,
      headWidth,
      headTop,
      headBottom: headTop + HEAD_HEIGHT,
    };
  }, [loadedStackHeight, stackBottomY]);

  const { port, starboard } = sideWeights(containerGroupedByYAxis);
  if (port + starboard <= 0) {
    return null;
  }
  const diff = Math.abs(port - starboard);
  const tiltsToPort = port >= starboard;
  const overLimit = diff > beamWarnings[beamWarnings.length - 1];
  const tiltDegrees =
    Math.min(diff / beamWarnings[beamWarnings.length - 1], 1) * TILT_MAX_DEGREES;
  const rotateDegrees = tiltsToPort ? -tiltDegrees : tiltDegrees;

  const direction = tiltsToPort ? t("port", "port") : t("starboard", "starboard");

  // The water line is derived from the actual draught (how much of the load is
  // still within the freeboard margin), not from the centre of gravity.
  let waterFrac = DEFAULT_WATER_FRAC;
  if (draughtCm > 0 && totalHeight > 0) {
    waterFrac = Math.min(Math.max(draughtCm / 100 / totalHeight, 0.08), 0.45);
  }
  const waterY = KEEL_Y - waterFrac * RANGE;
  const cogY = KEEL_Y - cogFrac * RANGE;

  const wavePath =
    `M 0 ${waterY}` +
    ` Q 10 ${waterY - 3} 20 ${waterY}` +
    ` T 40 ${waterY} T 60 ${waterY} T 80 ${waterY}` +
    ` T 100 ${waterY} T 120 ${waterY} T 140 ${waterY}` +
    ` T ${VIEW_W} ${waterY}` +
    ` L ${VIEW_W} ${VIEW_H} L 0 ${VIEW_H} Z`;

  const row1BottomY = Math.min(Math.max(stackBottomY, DECK_Y + 4), KEEL_Y - 6);
  const row1OuterLeft = HULL_DECK_LEFT + 1;
  const row1OuterRight = HULL_DECK_RIGHT - 1;

  const hullPath =
    `M ${HULL_DECK_LEFT} ${DECK_Y} ` +
    `L ${HULL_DECK_RIGHT} ${DECK_Y} ` +
    `L ${row1OuterRight} ${row1BottomY} ` +
    `L ${HULL_KEEL_RIGHT} ${KEEL_Y} ` +
    `Q ${CENTER_X} ${KEEL_Y + 6} ${HULL_KEEL_LEFT} ${KEEL_Y} ` +
    `L ${row1OuterLeft} ${row1BottomY} ` +
    `Z`;

  return (
    <>
      <Grid container justifyContent="center" padding="8px">
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" style={{ maxWidth: 190 }}>
          <defs>
            <linearGradient id="hullGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="65%" stopColor="#cbd5e1" />
              <stop offset="100%" stopColor="#8fa3b8" />
            </linearGradient>
          </defs>
          <path d={wavePath} fill="#f1f5f9" />

          <line
            x1="0"
            x2={VIEW_W}
            y1={waterY}
            y2={waterY}
            stroke="#94a3b8"
            strokeWidth="1.3"
            strokeDasharray="3 3"
          />

          <text x={HULL_DECK_LEFT - 14} y={waterY + 8} fontSize="8.5" fill="#ca4141" textAnchor="middle" fontWeight="700">
            {t("portInitial", "P")}
          </text>
          <text x={HULL_DECK_RIGHT + 14} y={waterY + 8} fontSize="8.5" fill="#44ca41" textAnchor="middle" fontWeight="700">
            {t("starboardInitial", "S")}
          </text>

          <g transform={`rotate(${rotateDegrees} ${CENTER_X} ${waterY})`}>
            <path d={hullPath} fill="url(#hullGradient)" stroke="#475569" strokeWidth="1.2" />
            <line
              x1={HULL_DECK_LEFT}
              x2={HULL_DECK_RIGHT}
              y1={DECK_Y}
              y2={DECK_Y}
              stroke="#334155"
              strokeWidth="1.2"
            />

            <rect
              x={CENTER_X - PILLAR_WIDTH / 2}
              y={wheelhouse.headBottom}
              width={PILLAR_WIDTH}
              height={DECK_Y - wheelhouse.headBottom}
              fill="#64748b"
              stroke="#334155"
              strokeWidth="1"
            />
            <rect
              x={wheelhouse.headX}
              y={wheelhouse.headTop}
              width={wheelhouse.headWidth}
              height={HEAD_HEIGHT}
              fill="#64748b"
              stroke="#334155"
              strokeWidth="1"
            />
            <rect
              x={wheelhouse.headX + 3}
              y={wheelhouse.headTop + 5}
              width={wheelhouse.headWidth - 6}
              height="4"
              fill="#f1f5f9"
            />
            <line
              x1={wheelhouse.headX - 2}
              x2={wheelhouse.headX + wheelhouse.headWidth + 2}
              y1={wheelhouse.headTop - 1}
              y2={wheelhouse.headTop - 1}
              stroke="#334155"
              strokeWidth="1.6"
            />

            {containerColumns.map(({ slot, x, width }) => {
              const layerCount = maxLayerBySlot[slot - 1];
              if (layerCount <= 0) return null;
              const boxes = [];
              const boxWidth = width - 2;
              const boxX = x + 1;
              for (let layer = 1; layer <= layerCount; layer += 1) {
                const y = stackBottomY - layer * boxHeight;
                const inHold = layer <= 2;
                boxes.push(
                  <rect
                    key={layer}
                    x={boxX}
                    y={y}
                    width={boxWidth}
                    height={boxHeight - 1}
                    fill={inHold ? "#cbd5e1" : "#94a3b8"}
                    fillOpacity={inHold ? 0.85 : 1}
                    stroke={inHold ? "#64748b" : "#475569"}
                    strokeWidth={inHold ? 0.8 : 0.7}
                    strokeDasharray={inHold ? "3 2" : undefined}
                  />
                );
              }
              return <g key={`slot-${slot}`}>{boxes}</g>;
            })}
          </g>

          <line
            x1={HULL_DECK_LEFT - 14}
            x2={HULL_DECK_RIGHT + 14}
            y1={MAX_COG_LINE_Y}
            y2={MAX_COG_LINE_Y}
            stroke="#64748b"
            strokeWidth="1.1"
            strokeDasharray="3 2"
          />

          <line
            x1={CENTER_X}
            x2={CENTER_X}
            y1={cogY}
            y2={KEEL_Y}
            stroke={cogColor}
            strokeWidth="1.8"
            strokeDasharray="3 2"
            opacity="0.9"
          />
          <line
            x1={CENTER_X - 24}
            x2={CENTER_X + 24}
            y1={cogY}
            y2={cogY}
            stroke={cogColor}
            strokeWidth="2.5"
          />
          <circle cx={CENTER_X} cy={cogY} r="2.4" fill={cogColor} />
          <rect
            x={CENTER_X - 21}
            y={Math.max(
              MAX_COG_LINE_Y + 2,
              Math.min(wheelhouse.headTop - 14, (MAX_COG_LINE_Y + wheelhouse.headTop) / 2 - 6)
            )}
            width="42"
            height="12"
            rx="2"
            fill={cogBgColor}
            stroke={cogColor}
            strokeWidth="0.6"
          />
          <text
            x={CENTER_X}
            y={Math.max(
              MAX_COG_LINE_Y + 2,
              Math.min(wheelhouse.headTop - 14, (MAX_COG_LINE_Y + wheelhouse.headTop) / 2 - 6)
            ) + 9}
            fontSize="8"
            fill={cogTextColor}
            textAnchor="middle"
            fontWeight="700"
          >
            KG {cogLoaded > 0 ? cogLoaded.toFixed(2) : "–"} m
          </text>
        </svg>
      </Grid>

      <Grid item display="flex" justifyContent="space-between" alignItems="center" padding="0 10px 6px">
        <Box sx={{ transform: "scale(0.7)", transformOrigin: "left center" }}>
          <SideWeightIndicator
            vessel={vessel}
            containerWeights={containerGroupedByYAxis as any}
            side={"port" as Side}
          />
        </Box>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color:
              diff < beamWarnings[0]
                ? "#9ca3af"
                : tiltsToPort
                ? "#dc2626"
                : "#16a34a",
          }}
        >
          {diff}T {diff === 0 ? "" : t("diff", "diff")}
        </Typography>
        <Box sx={{ transform: "scale(0.7)", transformOrigin: "right center" }}>
          <SideWeightIndicator
            vessel={vessel}
            containerWeights={containerGroupedByYAxis as any}
            side={"starboard" as Side}
          />
        </Box>
      </Grid>
    </>
  );
};

export default VesselTiltFrontProfile;